import { DefaultAllowedTags, TextFormatterDisplayWidget } from '@tableau/widgets';
import React from 'react';


import { NestedCSSProperties } from 'typestyle/lib/types';
import { divWithStyle } from './LayoutComponents';
import { cssClass } from './StyleFunctions';

const markupLineHeight = 1.5;
const markupLetterSpacing = 0.2;

const h1FontSize = 18;
const h2FontSize = 16;
const h3FontSize = 14;
const bodyFontSize = 13;

/**
 * Intended to be used in locations such as "database description" which shows up on the database
 * place page. Using H1 in textile there without these styles causes the rendered HTML to look
 * much more prominent than tableau's internal content, e.g.: the database title.
 *
 * These styles are based on the tableau design library and essentially use everything a step down
 * H1 in textile is rendered as a tableau H2 for example.
 *
 * For more information see: https://tableau.invisionapp.com/share/84WXCW9YSN9#/443420327_Artboard_Copy
 */
const MarkupPageStyles: NestedCSSProperties = {
  lineHeight: markupLineHeight,
  letterSpacing: markupLetterSpacing,
  fontSize: bodyFontSize,
  $nest: {
    h1: {
      fontSize: h1FontSize
    },
    h2: {
      fontSize: h2FontSize
    },
    h3: {
      fontSize: h3FontSize
    },
  }
};

export const MarkupPageStyleClass = cssClass('markup-page-style', MarkupPageStyles);
const MarkupPageStyle = divWithStyle('markupPageWrapper', MarkupPageStyles);

const modalMultiplier = 0.07;

/**
 * Intended to be used in locations such as "data quality warning" or "certification" which show up
 * as a popup pane and are generally in a constrained area.
 *
 * These styles are based on the tableau design library and essentially use everything a step down
 * H1 in textile is rendered as a tableau H2 for example.
 *
 * For more information see: https://tableau.invisionapp.com/share/84WXCW9YSN9#/443420327_Artboard_Copy
 */
const MarkupTooltipStyles: NestedCSSProperties = {
  lineHeight: markupLineHeight,
  letterSpacing: markupLetterSpacing,
  fontSize: bodyFontSize - (bodyFontSize * modalMultiplier),
  $nest: {
    h1: {
      fontSize: h1FontSize - (h1FontSize * modalMultiplier)
    },
    h2: {
      fontSize: h2FontSize - (h2FontSize * modalMultiplier)
    },
    h3: {
      fontSize: h3FontSize - (h3FontSize * modalMultiplier)
    },
  }
};

export const MarkupTooltipStyleClass = cssClass('markup-tooltip-style', MarkupTooltipStyles);
const MarkupTooltipStyle = divWithStyle('markupPaneWrapper', MarkupTooltipStyles);

export type FormattedMarkupWrapperProps = {
  readonly markup: string;
  readonly testId?: string;
  readonly linksDisabled?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;


export function FormattedMarkupWrapperInTooltip(props: FormattedMarkupWrapperProps): JSX.Element | null {
  const { linksDisabled, ...restProps } = props;
  const allowedTags = linksDisabled ? DefaultAllowedTags.filter(t => t !== 'a') : undefined;

  return (
    <MarkupTooltipStyle>
      <TextFormatterDisplayWidget {...restProps} allowedTags={allowedTags} />
    </MarkupTooltipStyle>
  );
}

export function FormattedMarkupWrapperInPage(props: FormattedMarkupWrapperProps): JSX.Element | null {
  const { linksDisabled, ...restProps } = props;
  const allowedTags = linksDisabled ? DefaultAllowedTags.filter(t => t !== 'a') : undefined;

  return (
    <MarkupPageStyle>
      <TextFormatterDisplayWidget {...restProps} allowedTags={allowedTags} />
    </MarkupPageStyle>
  );
}
